import { Component, EventEmitter, Input, Optional, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HelpersService } from 'src/app/services/helpers.service';
import { NotificationService } from 'src/app/services/notification.service';
import { JobcardService } from 'src/app/services/jobcard.service';
import { CyclistService } from 'src/app/services/cyclist.service';
import { MessageService } from 'primeng/api';
import moment from 'moment';
import { CommunicationService } from 'src/app/services/communication.service';

@Component({
  selector: 'app-workshop-jobcard-messaging-attachments',
  templateUrl: './workshop-jobcard-messaging-attachments.component.html',
  styleUrls: ['./workshop-jobcard-messaging-attachments.component.scss']
})
export class WorkshopJobcardMessagingAttachmentsComponent {

  @Input() jobcard: any;
  @Output() jobcardChange = new EventEmitter<any>();
  // Messages-only mode is used when hosted outside of a modal (e.g. in a sidebar)
  @Input() messagesOnly: boolean = false;
  @Output() close = new EventEmitter<void>();

  public countryData: any = {};

  public loadingMessages: boolean = false;
  public sendingMessage: boolean = false;
  public notifications: any[] = [];
  public messageText: string = '';
  public defaultMessageType: string = 'SMS';
  public isWhatsappCustomMessageWindowOpen: boolean = false;
  public whatsappFeatureEnabled: boolean = false;
  public hasInitiateChatTemplate: boolean = false;
  public busyInitiatingChat: boolean = false;

  public qrIdentifier: string = ""
  public url: string = "";
  public loadingAttachments: boolean = false;
  public attachments: any[] = [];
  public uploadQueue: any[] = []; // Track files being uploaded
  private uploadId: number = 0; // Generate unique IDs for uploads
  private mounted: boolean = false;
  public isDragOver: boolean = false;
  private dragCounter: number = 0;
  public interval: any = null;

  constructor(
    @Optional() public activeModal: NgbActiveModal,
    private translate: TranslateService,
    private helpersService: HelpersService,
    private notificationService: NotificationService,
    private jobcardService: JobcardService,
    private cyclistService: CyclistService,
    private messageService: MessageService,
    private communicationService: CommunicationService
  ) {
    this.countryData = this.helpersService.getCountryData();
  }

  ngOnInit() {
    this.mounted = true;
    // When hosted outside the modal, an injected NgbActiveModal would belong to a parent modal, so never use it
    if (this.messagesOnly || !this.activeModal) {
      this.activeModal = { dismiss: () => this.close.emit(), close: () => this.close.emit() } as any;
    }
    this.loadingMessages = true;
    this.checkIfWhatsappCustomMessageWindowOpen();
    this.getJobCardMessages();
    if (!this.messagesOnly) {
      this.loadingAttachments = true;
      this.getJobCardAttachments();
      this.getAttachmentsQRCodeData();
    }
    this.getMessagesForCustomer();
  }

  ngOnDestroy() {
    this.mounted = false;
    // Clear the interval to stop polling for messages
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  getJobCardMessages() {
    this.notificationService.getJobCardNotificationsV2(this.helpersService.getPartnerId(), this.jobcard.id).subscribe(results => {
      this.notifications = results.reverse();

      for (let notification of this.notifications) {
        if (!notification.MessageRead && notification.Direction === 'Incoming') {
          this.markAsRead(notification.ID);
        }
      }

      this.cleanDate();
      setTimeout(() => {
        this.updateScroll();
      }, 100);
      this.loadingMessages = false;
    }, err => {
      this.loadingMessages = false;
    });
  }

  markAsRead(messageID) {
    this.notificationService.markMessageRead(this.helpersService.getPartnerId(), messageID).subscribe(res => {
    });
  }

  updateScroll() {
    var element = document.getElementById('messages-content');
    element.scrollTop = element.scrollHeight;
  }

  cleanDate() {
    this.notifications.forEach((notification) => {
      notification.CreatedDate = notification.CreatedDate + 'Z';
    });
  }

  sendMessage() {
    if (
      this.messageText &&
      this.messageText.length > 0 &&
      !this.sendingMessage
    ) {
      this.sendingMessage = true;
      this.cyclistService
        .sendWorkshopMessage({
          Message: this.messageText,
          PartnerID: this.jobcard.partnerID,
          JobcardID: this.jobcard.id,
          ForceSMS: this.defaultMessageType == "SMS" ? true : false
        })
        .subscribe(
          (res) => {

            this.messageText = '';

            this.messageService.add({
              life: 5000,
              key: 'messages-attachments',
              detail: this.translate.instant('messages.message_queued')
            });
            this.getJobCardMessages();
            this.sendingMessage = false;

            //Check again in 5 seconds, allow time for delivery and webhooks to finish
            setTimeout(() => {
              this.getJobCardMessages();
              this.sendingMessage = false;
            }, 5000);
          },
          (error) => {
            this.sendingMessage = false;
            this.messageService.add({
              life: 5000,
              key: 'messages-attachments',
              detail: this.translate.instant('messages.error_sending_message'),
            });
          },
        );
    }
  }

  checkIfWhatsappCustomMessageWindowOpen() {
    this.notificationService.checkIfWhatsappCustomMessageWindowOpen(this.jobcard.partnerID, "JOBCARD", this.jobcard.id).subscribe(res => {
      this.whatsappFeatureEnabled = res.useWhatsappFeatures;
      this.isWhatsappCustomMessageWindowOpen = res.isMessageWindowOpen;
      this.hasInitiateChatTemplate = res.hasInitiateChatTemplate;
      if (this.whatsappFeatureEnabled && (this.hasInitiateChatTemplate || this.isWhatsappCustomMessageWindowOpen)) {
        this.defaultMessageType = "WHATSAPP";
      }
    })
  }

  sendInitiateWhatsappChatTemplate() {
    this.busyInitiatingChat = true;
    this.notificationService.sendInitiateWhatsappChatTemplate(this.jobcard.partnerID, "JOBCARD", this.jobcard.id).subscribe(res => {
      this.getJobCardMessages();
      this.busyInitiatingChat = false;
      //Check again in 5 seconds, allow time for delivery and webhooks to finish
      setTimeout(() => {
        this.getJobCardMessages();
        this.sendingMessage = false;
      }, 5000);
    }, err => {
      this.busyInitiatingChat = false;
    })
  }

  shouldShowDateSeparator(notification: any, index: number): boolean {
    if (index === 0) return true; // Always show for first message

    const currentDate = new Date(notification.CreatedDate).toDateString();
    const previousDate = new Date(this.notifications[index - 1].CreatedDate).toDateString();

    return currentDate !== previousDate;
  }

  getDateDisplayText(dateString: string): string {
    const messageDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Compare just the date part
    if (messageDate.toDateString() === today.toDateString()) {
      return this.translate.instant('bikeshop.workshop.jobcard.manage.messaging.today');
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return this.translate.instant('bikeshop.workshop.jobcard.manage.messaging.yesterday');
    } else {
      return moment(messageDate).format(this.countryData.MomentDateFormat);
    }
  }

  trackByNotification(index: number, notification: any): any {
    return notification.ID || index;
  }








  getAttachmentsQRCodeData() {
    this.jobcardService.getJobCardQRIdentifier(this.jobcard.id).subscribe(result => {
      this.url = window.location.origin + '/cyclist/jobcardAttachments/' + this.jobcard.id + '?QRCodeIdentifier=' + result.qrCodeIdentifier + "&Theme=" + this.jobcard.partnerID;
    })
  }


  getJobCardAttachments() {
    this.jobcardService.getJobCardAttachmentsV1(this.jobcard.id, this.jobcard.CustomerID, this.jobcard.PartnerID).subscribe(results => {
      var attachments = [];
      if (results && results.length > 0) {
        if (results[0].attachments) {
          results.forEach(jobcard => {
            if (jobcard && jobcard.attachments && jobcard.attachments.length > 0) {
              jobcard.attachments.forEach(attachment => {
                attachments.push(attachment);
              })
            }
          });
        } else {
          attachments = results;
        }
      }
      this.attachments = attachments;
      this.loadingAttachments = false;
    }, err => {
      this.loadingAttachments = false;
    });
    setTimeout(() => {
      if (this.mounted) {
        this.getJobCardAttachments();
      }
    }, 10000);
  }

  // deleteAttachment(attachment, event) {
  //   event.stopPropagation();
  //   this.loadingAttachments = true;
  //   this.jobcardService.deleteJobCardAttachment(attachment.id).subscribe(attachments => {
  //     this.attachments = attachments;
  //     this.loadingAttachments = false;
  //   })
  // }

  updateFileName(event, attachment) {

    var data = {
      ID: attachment.id,
      ServiceID: attachment.serviceID,
      FileName: attachment.fileName,
    };
    this.jobcardService.updateJobCardAttachment(data).subscribe((result) => {
      event.deactivate();
    });
  }

  readFileAsync(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result.toString());
      };
      reader.onerror = reject;
    })
  }

  openAttachment(item) {
    window.open(item.mediaURL, "_blank")
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer && event.dataTransfer.types.includes('Files')) {
      event.dataTransfer.dropEffect = 'copy';
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    // Only hide if leaving the container completely
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const isOutside = event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (isOutside) {
      this.isDragOver = false;
    }
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const mockEvent = {
        target: { files: files }
      };
      this.addAttachment(mockEvent);
    }
  }

  async addAttachment(event) {
    if (event.target.files && event.target.files.length > 0) {
      const maxFileSize = 20 * 1024 * 1024; // 20MB limit
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

      for (var i = 0; i < event.target.files.length; i++) {
        var file = event.target.files[i];

        // Validate file size
        if (file.size > maxFileSize) {
          this.messageService.add({
            severity: 'warn',
            key: 'messages-attachments',
            detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.file_too_large', {
              filename: file.name,
              maxSize: '20MB'
            }),
            life: 5000
          });
          continue;
        }

        // Validate file type
        if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
          this.messageService.add({
            severity: 'warn',
            key: 'messages-attachments',
            detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.unsupported_file_type', {
              filename: file.name
            }),
            life: 5000
          });
          continue;
        }

        // Add to upload queue immediately for UI feedback
        const uploadItem = {
          id: `upload_${++this.uploadId}`, // Temporary ID
          fileName: file.name,
          mediaType: file.type,
          mediaURL: '', // Will be set after upload
          file: file, // Keep reference to original file
          isUploading: true,
          uploadProgress: 0,
          uploadError: false
        };

        // Create preview URL for images
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            uploadItem.mediaURL = e.target?.result as string;
          };
          reader.readAsDataURL(file);
        }

        this.uploadQueue.push(uploadItem);

        // Start upload process
        this.uploadFile(uploadItem);
      }
    }

    // Clear the input
    if (event.target) {
      event.target.value = '';
    }
  }

  private async uploadFile(uploadItem: any) {
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        if (uploadItem.uploadProgress < 90) {
          uploadItem.uploadProgress += Math.random() * 20;
        }
      }, 200);

      // Read file data
      const data = await this.readFileAsync(uploadItem.file);

      const attachment = {
        ServiceLogID: 0,
        ServiceID: this.jobcard.id,
        MediaType: uploadItem.mediaType,
        MediaData: data,
        FileName: uploadItem.fileName
      };

      // Complete progress to 90% before API call
      uploadItem.uploadProgress = 90;

      this.jobcardService.addJobCardAttachment(attachment).subscribe(
        (attachments) => {
          clearInterval(progressInterval);

          // Remove upload item from queue and attachments array
          this.removeUploadItem(uploadItem);

          // Add the real attachments from server
          if (attachments && attachments.length > 0 && attachments[0].attachments) {
            this.attachments = [...attachments.map(a => a.attachments).flat()];
          } else {
            this.attachments = [...attachments];
          }

          uploadItem.uploadProgress = 100;

          this.messageService.add({
            severity: 'success',
            key: 'messages-attachments',
            detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.file_uploaded', {
              filename: uploadItem.fileName
            }),
            life: 3000
          });
        },
        (error) => {
          clearInterval(progressInterval);

          // Mark as error but keep in UI for retry option
          uploadItem.uploadError = true;
          uploadItem.isUploading = false;

          this.messageService.add({
            severity: 'error',
            key: 'messages-attachments',
            detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.upload_failed', {
              filename: uploadItem.fileName
            }),
            life: 5000
          });
        }
      );
    } catch (error) {
      uploadItem.uploadError = true;
      uploadItem.isUploading = false;

      this.messageService.add({
        severity: 'error',
        key: 'messages-attachments',
        detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.file_read_error', {
          filename: uploadItem.fileName
        }),
        life: 5000
      });
    }
  }

  private removeUploadItem(uploadItem: any) {
    // Remove from upload queue
    const queueIndex = this.uploadQueue.findIndex(item => item.id === uploadItem.id);
    if (queueIndex > -1) {
      this.uploadQueue.splice(queueIndex, 1);
    }

    // Remove from attachments array
    const attachmentIndex = this.attachments.findIndex(item => item.id === uploadItem.id);
    if (attachmentIndex > -1) {
      this.attachments.splice(attachmentIndex, 1);
    }
  }

  retryUpload(uploadItem: any) {
    uploadItem.uploadError = false;
    uploadItem.isUploading = true;
    uploadItem.uploadProgress = 0;
    this.uploadFile(uploadItem);
  }

  cancelUpload(uploadItem: any) {
    this.removeUploadItem(uploadItem);
  }

  deleteAttachment(attachment, event) {
    event.stopPropagation();

    // Check if it's an uploading item
    if (attachment.isUploading) {
      this.cancelUpload(attachment);
      return;
    }

    // Remove from attachments array immediately for better UX
    const attachmentIndex = this.attachments.findIndex(item => item.id === attachment.id);
    if (attachmentIndex > -1) {
      this.attachments.splice(attachmentIndex, 1);
    }

    // Then make the API call to delete on server
    this.loadingAttachments = true;
    this.jobcardService.deleteJobCardAttachment(attachment.id).subscribe(
      (attachments) => {
        // Update with server response (in case server has different state)
        this.getJobCardAttachments();
        this.loadingAttachments = false;

        this.messageService.add({
          severity: 'success',
          key: 'messages-attachments',
          detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.file_deleted', {
            filename: attachment.fileName
          }),
          life: 3000
        });
      },
      (error) => {
        // On error, add the item back to the array
        this.attachments.splice(attachmentIndex, 0, attachment);
        this.loadingAttachments = false;

        this.messageService.add({
          severity: 'error',
          key: 'messages-attachments',
          detail: this.translate.instant('bikeshop.workshop.jobcard.manage.attachments.delete_failed', {
            filename: attachment.fileName
          }),
          life: 5000
        });
      }
    );
  }

  // Helper method to check if item is uploading
  isUploadingItem(item: any): boolean {
    return item.hasOwnProperty('isUploading') && item.isUploading;
  }

  // Helper method to check if item has upload error
  hasUploadError(item: any): boolean {
    return item.hasOwnProperty('uploadError') && item.uploadError;
  }

  //q: Create a function that will call the getMessagesForCustomer function in the communicatioService every 30 seconds
  getMessagesForCustomer() {
    this.interval = setInterval(() => {
      this.communicationService
        .getNewMessagesForCustomer(this.jobcard.partnerID, this.jobcard.customer.ID)
        .subscribe((res) => {
          this.getJobCardMessages();
        });
    }, 15000);
  }

}
